import React, { useState, useEffect, useRef, useCallback } from "react"
import { Helmet } from "react-helmet"
import { ScrollContext } from "../context"
import { CATALOG } from "../data/catalog"
import { minmax } from "../utils/minmax"
import { animated, useSpring } from "react-spring"
import get from "lodash/get"
import debounce from "lodash/debounce"
import Positioner from "../components/Positioner"
import Menu from "../components/Menu/Menu"
import MenuContent from "../components/Menu/MenuContent"
import { useStaticQuery, graphql } from "gatsby"
import metaTags from './commonMeta'

const Hammer = typeof window !== "undefined" ? require("hammerjs") : null

const mc = typeof document !== "undefined" ? new Hammer(document) : undefined
mc && mc.get("pan").set({ direction: Hammer.DIRECTION_ALL })

const MenuSize = typeof window !== "undefined" ? (window.innerWidth - 64 - 24 * 11) / 12 * 5 + 24 * 5 + 32 : 0

const computeBoundaries = (x, y, after) => {
  const pages = Object.values(CATALOG)
  const hasAbovePage = pages.some(page => page.x === x && page.y === y - 1)
  const hasBelowPage = pages.some(page => page.x === x && page.y === y + 1)
  const hasLeftSidePage = pages.some(page => page.x === x - 1 && page.y === y)
  const hasRightSidePage = pages.some(page => page.x === x + 1 && page.y === y)
  const isYMoving = y !== Math.floor(y)
  const isXMoving = x !== Math.floor(x)
  const next = {
    minY: Math.max(0, !isYMoving && hasAbovePage ? Math.floor(y) - 1 : Math.floor(y)),
    maxY: hasBelowPage ? Math.ceil(y) + 1 : Math.ceil(y),
    minX: Math.max(0, !isXMoving && hasLeftSidePage ? Math.floor(x) - 1 : Math.floor(x)),
    maxX: hasRightSidePage ? Math.ceil(x) + 1 : Math.ceil(x)
  }
  if (after) {
    after(next)
  } else {
    return next
  }
}

const computeBoundariesDebounced = debounce(computeBoundaries, 1000, { leading: true })

const TemplateWrapper = ({ isMobile, children, location, ...props }) => {
  const currentItem = CATALOG[location.pathname]
  const [[x, y], setPos] = useState([get(currentItem, "x", 0), get(currentItem, "y", 0)])
  const [p, setP] = useState([get(currentItem, "x", 0), get(currentItem, "y", 0)])
  const domRef = useRef(null)
  const [boundaries, setBoundaries] = useState(computeBoundaries(x, y))
  const [isMenuOpen, setMenuOpen] = useState(false)
  const isImmediate = useRef(true)
  const [showFader, setShowFader] = useState(false)

  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            siteUrl,
            siteImage,
          }
        }
      }
    `)
  const siteUrl = get(site, 'siteMetadata.siteUrl')
  const siteImage = get(site, 'siteMetadata.siteImage')

  useEffect(() => {
    if (typeof window === "undefined") {
      return () => { }
    }
    const handler = e => {
      setPos(pos => {
        const [x, y] = pos
        const { minX, minY, maxX, maxY } = boundaries

        return [
          minmax(x + 0.09 * Math.sign(e.deltaY), minX, maxX),
          minmax(y + 0.09 * Math.sign(e.deltaY), minY, maxY)
        ]
      })
    }

    window.addEventListener("wheel", handler)
    return () => {
      window.removeEventListener("wheel", handler)
    }
  }, [boundaries])

  useEffect(() => {
    computeBoundariesDebounced(x, y, setBoundaries)
  }, [x, y])

  const spring = useSpring({
    to: { x, y },
    onChange: ({ value: { x, y } }) => {
      if (Math.round(y) !== p[1] || Math.round(x) !== p[0]) {
        setP([Math.round(x), Math.round(y)])
      }
    },
    onRest: () => {
      isImmediate.current = false
    },
    immediate: isImmediate.current
  })

  const menuSpring = useSpring({
    from: { offsetX: 0 },
    to: { offsetX: isMenuOpen ? MenuSize : 0 },
  })

  const opacitySpring = useSpring({
    from: { opacity: showFader ? 1 : 0 },
    to: { opacity: showFader ? 0 : 1 },
    config: {
      duration: 1000,
    }
  })

  const navigate = useCallback((pathname) => {
    const currentItem = CATALOG[pathname]
    const nextX = get(currentItem, "x", 0)
    const nextY = get(currentItem, "y", 0)
    if (p[0] !== nextX || p[1] !== nextY) {
      isImmediate.current = true
      setShowFader(true)
      setPos([nextX, nextY])
      setP([nextX, nextY])
      setBoundaries(computeBoundaries(nextX, nextY))
      setMenuOpen(false)
      setTimeout(() => setShowFader(false), 1000)
    }
  }, [p])

  return (
    <div
      className={`desktop-root ${isMobile ? "isMobile": "isDesktop"}`}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: "hidden"
      }}
    >
      <animated.div
        style={{
          position: "absolute",
          top: 0,
          left: menuSpring.offsetX.interpolate(o => o - MenuSize),
          right: menuSpring.offsetX.interpolate(o => MenuSize - o),
          bottom: 0,
        }}
        className="grid-cols-12"
      >
        <div className="col-5">
          <MenuContent navigate={navigate} />
        </div>
      </animated.div>
      <animated.div
        ref={domRef}
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: menuSpring.offsetX,
          right: menuSpring.offsetX.interpolate(o => -o),
          margin: 16,
          borderColor: "#005BBB",
          borderStyle: "solid",
          borderWidth: 2,
          borderRadius: 32,
        }}>
        <Helmet
          title="L'e-mobility per il business"
          meta={metaTags(siteUrl)}
        />
        <ScrollContext.Provider value={{
          ...spring,
          p,
          h: get(domRef.current, "offsetHeight", 0),
          w: get(domRef.current, "offsetWidth", 0),
          toX: x,
          toY: y,
        }}>
          <Positioner location={location} navigate={navigate} />
          {showFader && (
            <animated.div className="XXXXXXXXXXXX" style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              ...opacitySpring,
              backgroundColor: "black",
              zIndex: 10000
            }}
            />
          )}
        </ScrollContext.Provider>
      </animated.div>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          pointerEvents: "none",
          zIndex: 10000,
          margin: 18,
        }}
      >
        <div className="grid-cols-12 w-100 h-100">
          <div className="col-1">
            <Menu isOpen={isMenuOpen} onClick={() => setMenuOpen(!isMenuOpen)} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default TemplateWrapper
